<template>
  <h1 class="test-header">
    <!-- Each separator closes the segment before it, joined by a no-break space,
         so a wrap never opens a line with one. The spaces between segments
         stay on one line: the template compiler drops whitespace that spans a
         line break. -->
    <span class="test-header-id">
      <span>Test {{ testId }}</span>
      <button
        type="button"
        class="test-header-copy"
        title="Copy ID"
        aria-label="Copy ID"
        data-copy-anchor
        data-copy-placement="beside"
        @click="copyToClipboard(testId, $event, 'ID')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
          <path fill-rule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2Zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H6ZM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1H2Z"></path>
        </svg>
      </button>
      <span v-if="hasBusinessName || hasUuid">&nbsp;·</span>
    </span> <span v-if="hasBusinessName" class="test-header-business">{{ businessNameText }}<span class="test-header-join">{{ businessNameTrailingSpace }}<template v-if="hasUuid">&nbsp;·</template></span></span> <span v-if="hasUuid" class="test-header-uuid-value">
      <span>{{ uuid }}</span>
      <button
        type="button"
        class="test-header-copy"
        title="Copy Test ID"
        aria-label="Copy Test ID"
        data-copy-anchor
        data-copy-placement="beside"
        @click="copyToClipboard(uuid, $event, 'Test ID')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
          <path fill-rule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2Zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H6ZM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1H2Z"></path>
        </svg>
      </button>
    </span>
  </h1>
</template>

<script>
import copyToClipboard from "@/mixins/copyToClipboard";

export default {
  name: "Admin_Tests_Header",
  mixins: [copyToClipboard],
  props: {
    // Taken from the route, never from a response, so it shows before any
    // request returns and while one fails.
    testId: {
      type: [String, Number],
      required: true,
    },
    businessName: {
      type: String,
      default: null,
    },
    uuid: {
      type: String,
      default: null,
    },
  },
  computed: {
    // A backend that predates business_name, a failed or pending detail
    // request, and a test with no business all leave this false, and the
    // header shows the id alone. Safe because nothing else reads the name.
    hasBusinessName() {
      return typeof this.businessName === "string" && this.businessName.trim() !== "";
    },
    // The name is split only so that whitespace at its end sits inside the
    // no-wrap join with the separator: browsers break after a space even when a
    // no-break space follows, and the separator would open the next line.
    // Every character is still shown, in order.
    businessNameText() {
      return this.hasBusinessName ? this.businessName.trimEnd() : "";
    },
    businessNameTrailingSpace() {
      return this.businessNameText ? this.businessName.slice(this.businessNameText.length) : "";
    },
    // Null for many older tests, and absent from a backend that predates the
    // field; either way the segment and its separator are left out, which is
    // safe because nothing else reads the UUID.
    hasUuid() {
      return typeof this.uuid === "string" && this.uuid.trim() !== "";
    },
  },
};
</script>

<style scoped>
/* Sized and weighted above a FormSection title (17px, 700) so it reads as the
   page heading, and kept free of card, border and background so it does not
   read as another section. The margins are the product owner's, set by eye on
   the review screen. */
.test-header {
  margin: 2rem 3% 16px;
  padding: 0;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.3;
  color: #4d4d4d;
  background: none;
  border: 0;
  overflow-wrap: anywhere;
}

/* Keeps each copy button on the same line as its value, and the id's
   separator with them, when the line wraps. */
.test-header-id,
.test-header-uuid-value {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  vertical-align: baseline;
}

.test-header-join {
  white-space: nowrap;
}

/* Secondary to the id and name through size only; it keeps the header's
   colour and weight, because a second grey or a lighter weight reads as a
   mismatch. */
.test-header-uuid-value {
  font-size: 18px;
}

.test-header-copy {
  margin-left: 6px;
  background: none;
  border: 0;
  padding: 2px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: #666;
  opacity: 0.8;
  transition: all 0.2s ease;
}

.test-header-copy:hover,
.test-header-copy:focus-visible {
  opacity: 1;
  color: #04a394;
}
</style>
